/**
 * Scroll-depth, desktop exit-intent, and coarse-pointer mobile “scroll toward top” heuristic.
 * Fires at most once per attachment; removes listeners after fire.
 */
import type { EceModalTrigger } from '../analytics/data-layer';

/** Must have scrolled at least this far down the page before mobile exit can arm (engagement). */
const MOBILE_ARM_SCROLL_RATIO = 0.45;
/** Top portion of the scroll range — user “landed” here after scrolling up from below. */
const MOBILE_TOP_BAND_RATIO = 0.18;
const MOBILE_DWELL_MS = 3500;
/** Minimum upward movement in the recent window (px). */
const MOBILE_MIN_RECENT_UP_PX = 100;
/**
 * Rolling window for upward scroll: a physical flick often takes longer than a single short “burst”
 * but is still one intent. Samples older than this are dropped (slow drift does not accumulate).
 */
const MOBILE_RECENT_UP_WINDOW_MS = 1000;

export interface TriggerAttachOptions {
  /** 0–1, e.g. 0.5 for 50% scroll depth */
  scrollDepthRatio: number;
  /** Called at most once total when a trigger fires and session allows */
  onFire: (detail: { trigger: EceModalTrigger }) => void;
  /** If true, triggers will not register */
  isSuppressed: () => boolean;
}

/**
 * Registers scroll and mouse listeners. Call the returned function to detach without firing (e.g. tests).
 */
export function attachModalTriggers(options: TriggerAttachOptions): () => void {
  const disposers: Array<() => void> = [];

  let fired = false;
  const tryFire = (trigger: EceModalTrigger) => {
    if (fired || options.isSuppressed()) return;
    fired = true;
    dispose();
    options.onFire({ trigger });
  };

  const dispose = () => {
    while (disposers.length) {
      const d = disposers.pop();
      d?.();
    }
  };

  const coarse = window.matchMedia('(pointer: coarse)');
  const pageEnteredAt = Date.now(); /* dwell for mobile + desktop exit intent */
  let maxScrollRatio = 0;
  let lastScrollTop = document.documentElement.scrollTop;
  /** Timestamps + px for upward-only deltas; pruned to a rolling window. */
  let recentUpSamples: Array<{ t: number; px: number }> = [];

  /* ---- scroll depth + mobile rapid scroll-up (coarse pointer) ---- */
  const onScroll = () => {
    if (options.isSuppressed()) return;
    const el = document.documentElement;
    const st = el.scrollTop;
    const scrollable = el.scrollHeight - el.clientHeight;
    const ratio = scrollable <= 0 ? 1 : st / scrollable;
    maxScrollRatio = Math.max(maxScrollRatio, ratio);

    if (coarse.matches) {
      const delta = st - lastScrollTop;
      lastScrollTop = st;
      const now = performance.now();

      if (delta < 0) {
        recentUpSamples.push({ t: now, px: -delta });
      } else if (delta > 8) {
        recentUpSamples = [];
      }

      const cutoff = now - MOBILE_RECENT_UP_WINDOW_MS;
      recentUpSamples = recentUpSamples.filter((s) => s.t >= cutoff);
      const recentUpPx = recentUpSamples.reduce((a, s) => a + s.px, 0);

      const dwellOk = Date.now() - pageEnteredAt > MOBILE_DWELL_MS;
      const armed = maxScrollRatio >= MOBILE_ARM_SCROLL_RATIO;
      const inTopBand = ratio <= MOBILE_TOP_BAND_RATIO;
      const recentUpOk = recentUpPx >= MOBILE_MIN_RECENT_UP_PX;

      if (dwellOk && armed && inTopBand && recentUpOk) {
        tryFire('mobile_scroll_exit');
        return;
      }
    } else {
      lastScrollTop = st;
    }

    if (ratio >= options.scrollDepthRatio) tryFire('scroll_depth');
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  disposers.push(() => window.removeEventListener('scroll', onScroll));
  onScroll();

  /* ---- exit intent (fine pointer / desktop heuristic) ---- */
  const fine = window.matchMedia('(pointer: fine)');
  let lastY = 0;

  const onMove = (e: MouseEvent) => {
    if (!fine.matches || options.isSuppressed()) return;
    const dy = e.clientY - lastY;
    lastY = e.clientY;
    const dwellOk = Date.now() - pageEnteredAt > 3500;
    // Upward movement toward browser chrome: small clientY and negative delta
    if (dwellOk && e.clientY < 72 && dy < -8) tryFire('exit_intent');
  };

  const onOut = (e: MouseEvent) => {
    if (!fine.matches || options.isSuppressed()) return;
    if (e.relatedTarget != null) return;
    if (e.clientY < 100 && Date.now() - pageEnteredAt > 2500) tryFire('exit_intent');
  };

  document.addEventListener('mousemove', onMove, { passive: true });
  document.documentElement.addEventListener('mouseout', onOut);
  disposers.push(() => document.removeEventListener('mousemove', onMove));
  disposers.push(() => document.documentElement.removeEventListener('mouseout', onOut));

  return dispose;
}
